import React, { useEffect } from 'react';

import { SmBadge, SmButton, SmIcon } from '@smiles/smiles-react-design-system';
import Mustache from 'mustache';
import { TScreens } from '@/containers/Modal';

import useApi from '@/hooks/useApi';
import useEvent from '@/hooks/useEvent';
import usePurchase from '@/hooks/usePurchaseData';

import { Counter } from '@/components/Counter';

import { TModalContent } from '@/services/requests/Liferay/types';
import { dispatchInteraction } from '@/services/requests/Interactions/helpers';

import { clearHtmlTags } from '@/helpers/HtmlHelper/clearHtmlTags';

type TProps = {
  onBack: () => void;
  liferayContent: TModalContent;
  changeScreen: (newScreen: TScreens) => void;
};

export const ChangingNumberMiles = ({
  onBack,
  liferayContent,
  changeScreen,
}: TProps) => {
  const {
    title,
    badge,
    quantityLabel,
    minLabel,
    maxLabel,
    chooseVoucherLink,
    ruleBtn,
    continueBtn,
  } = liferayContent;

  const {
    currentVoucher,
    voucherList,
    getListQuantityMilesToPurchase,
    milesToPurchase,
  } = usePurchase();

  useEffect(() => {
    if (!currentVoucher) {
      changeScreen('error');
    }
  }, [currentVoucher, changeScreen]);

  if (!currentVoucher) return null;

  const { ApiInteractions } = useApi();
  const event = useEvent();

  const handleRedirectToVoucherScreen = () => {
    dispatchInteraction(
      ApiInteractions,
      'extra_mpv_value_click_other_voucher',
      {
        recommendationId: event.data.recommendationId,
      },
    );
    changeScreen('voucherlist');
  };

  const handleClickRules = () => {
    dispatchInteraction(ApiInteractions, 'extra_mpv_value_click_rules', {
      recommendationId: event.data.recommendationId,
    });
    changeScreen('rules');
  };

  const handleClickContinue = (): void => {
    dispatchInteraction(ApiInteractions, 'extra_mpv_value_click_continue', {
      recommendationId: event.data.recommendationId,
    });

    changeScreen('finishOrder');
  };

  return (
    <div
      className="smls-b2c-modals__modal"
      data-testid="screen-changing-number-miles"
    >
      <div className="smls-b2c-modals__button--back">
        <SmButton
          onClick={onBack}
          isPill
          isOutlined
          icon={<span className="material-icons">arrow_back_ios</span>}
        />
      </div>
      {title?.text && (
        <p
          className="smls-b2c-modals__modal--title" // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: title.text }}
        />
      )}
      {badge && (
        <div className="smls-b2c-modals__modal--badge">
          <SmBadge
            icon={<SmIcon name="check_circle" size="small" />}
            size="medium"
            text={clearHtmlTags(Mustache.render(badge.text, currentVoucher))}
            variant="success"
          />
        </div>
      )}
      <div className="smls-b2c-modals__modal--card-counter">
        <Counter
          decreaseIconTitle={clearHtmlTags(minLabel?.text || '')}
          id="counter-id"
          increaseIconTitle={clearHtmlTags(maxLabel?.text || '')}
          label={clearHtmlTags(quantityLabel?.text || '')}
          max={currentVoucher.availableMiles}
          min={1000}
          step={1000}
          minLabel={clearHtmlTags(minLabel?.text || '')}
          maxLabel={clearHtmlTags(maxLabel?.text || '')}
          liferayContent={liferayContent}
        />
      </div>
      <div className="smls-b2c-modals__modal--choose-voucher">
        {voucherList.milesPurchase.length > 1 && chooseVoucherLink?.text && (
          <button
            data-testid="choose-voucher-link"
            type="button"
            title={clearHtmlTags(chooseVoucherLink.text)}
            className="smls-b2c-modals__modal--choose-voucher--link"
            aria-label={clearHtmlTags(chooseVoucherLink.text)}
            onClick={handleRedirectToVoucherScreen}
          >
            {clearHtmlTags(chooseVoucherLink.text)}
          </button>
        )}
      </div>
      <div className="smls-b2c-modals__modal--buttons">
        {ruleBtn?.text && (
          <SmButton
            className="smls-b2c-modals__modal--buttons--rules"
            loadingSize="large"
            text={clearHtmlTags(ruleBtn.text)}
            variant="primary"
            isOutlined
            onClick={handleClickRules}
          />
        )}
        {continueBtn?.text && (
          <SmButton
            className="smls-b2c-modals__modal--buttons--continue"
            loadingSize="large"
            text={clearHtmlTags(continueBtn.text)}
            variant="primary"
            disabled={
              !getListQuantityMilesToPurchase().includes(milesToPurchase)
            }
            onClick={handleClickContinue}
          />
        )}
      </div>
    </div>
  );
};
